import { useEffect, useRef, useState } from 'react';
import { TRACKED_REG } from '../lib/flightTrack';
import { Link } from 'react-router-dom';

const TRACK_API = `${import.meta.env.BASE_URL}api/track`;
const HISTORY_API = `${import.meta.env.BASE_URL}api/track-history`;
/**
 * 30s, deliberately unchanged now that this mounts app-wide rather than on two
 * dashboards. Every signed-in session polls this the whole time it is open, so
 * halving the interval to fill the trace faster would have doubled the load on
 * the proxy for a minute of impatience.
 */
const POLL_MS = 30_000;
/** 30 samples × 30s = the last 15 minutes of altitude. */
const HISTORY = 30;
/**
 * How stale the newest history sample may be before we ignore the backfill.
 * adsb.lol's trace runs 15-20 minutes behind live, so this has to be generous;
 * it exists only to reject a trace left over from an earlier flight.
 */
const SEED_MAX_AGE_MS = 45 * 60 * 1000;
/** Below this the aircraft is level for our purposes and we say nothing. */
const RATE_DEADBAND_FPM = 100;

type Live = {
  alt: number | null;
  gs: number;
  /** Degrees true. null when the feed doesn't carry it — then we don't rotate. */
  track: number | null;
  /** Feet per minute, + is up. */
  rate: number | null;
};

/**
 * Shows up only while the aircraft is actually in the air.
 *
 * The whole point is the empty state: when N8366X is on the ramp there is no
 * pill, no grey "on the ground" chip, nothing. A header that says something
 * only when something is happening is worth glancing at; one that always has a
 * badge in it becomes furniture and stops being read.
 *
 * That also makes the failure mode the safe one. adsb.lol only sees the
 * aircraft when its transponder is being received by a nearby feeder, so a
 * low VFR hop can go untracked — and an untracked flight simply shows nothing,
 * which is exactly what the ground state looks like. We can miss a flight. We
 * can never claim one that isn't happening.
 *
 * Everything in it is live telemetry rather than decoration: the aeroplane is
 * rotated to the real ground track, the trace is the altitudes we have actually
 * polled, and the climb rate is the feed's baro_rate. The trace therefore needs
 * a few polls before it can draw — roughly a minute — and simply isn't rendered
 * until then instead of drawing a made-up line.
 */
export function AirbornePill({
  reg = TRACKED_REG,
  to = '/tracker',
  className = '',
}: {
  reg?: string;
  to?: string;
  /** Spacing is the caller's business. The pill renders nothing when parked, so
   *  margins live here rather than on a wrapper that would leave a gap. */
  className?: string;
}) {
  const [live, setLive] = useState<Live | null>(null);
  const [trace, setTrace] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    // One seed per flight. Reset on landing so the next flight seeds again.
    let seeded = false;

    /**
     * Draw the line immediately instead of making someone hold the page open for
     * two polls. adsb.lol keeps the day's trace, so on the first airborne poll we
     * backfill the last 15 minutes of altitude and let the live polls extend it.
     * The trace lags a few minutes behind real time — fine for a shape, which is
     * all this is. If it fails we simply start from empty as before.
     */
    async function seed() {
      if (seeded) return;
      seeded = true;
      try {
        const res = await fetch(`${HISTORY_API}?reg=${encodeURIComponent(reg)}`, {
          cache: 'no-store',
        });
        const data = await res.json();
        const points: { t?: number; alt?: number | null }[] = data?.points ?? [];
        // Take the trailing run of airborne samples — that run IS the current
        // flight, since the aircraft is on the ground (alt null) either side of it.
        // Selecting by wall-clock instead would return nothing, because the trace
        // is published 15-20 minutes behind live.
        const flight: number[] = [];
        for (let i = points.length - 1; i >= 0; i--) {
          const p = points[i];
          if (typeof p?.alt !== 'number') break;
          flight.unshift(p.alt);
        }
        const newest = points[points.length - 1]?.t;
        const stale = typeof newest !== 'number' || Date.now() - newest > SEED_MAX_AGE_MS;
        if (cancelled || stale || flight.length < 2) return;
        const seeded_alts = downsample(flight, HISTORY - 1);
        // Live samples that already landed stay on the right-hand end.
        setTrace((prev) => [...seeded_alts, ...prev].slice(-HISTORY));
      } catch {
        // No backfill available — the line fills in from live polls instead.
      }
    }

    async function poll() {
      let next: Live | null = null;
      try {
        const res = await fetch(`${TRACK_API}?reg=${encodeURIComponent(reg)}`, {
          cache: 'no-store',
        });
        const data = await res.json();
        const ac = data?.ac?.[0];
        // Upstream failure (rate limit/outage): hold what we had rather than
        // drawing a landing that didn't happen. Next poll decides.
        if (data?.error && !ac) {
          if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
          return;
        }
        // alt_baro comes back as the string 'ground' when it's sitting there.
        const airborne = ac && ac.alt_baro !== 'ground' && ac.alt_baro != null;
        if (airborne) {
          next = {
            alt: ac.alt_baro,
            gs: Math.round(ac.gs ?? 0),
            track: typeof ac.track === 'number' ? ac.track : null,
            rate: typeof ac.baro_rate === 'number' ? ac.baro_rate : null,
          };
        }
      } catch {
        // A failed lookup is indistinguishable from parked, and should look
        // the same: we say nothing rather than guess.
        next = null;
      }

      if (cancelled) return;
      if (next) seed();
      else seeded = false;
      setLive(next);
      // A landing clears the trace, so the next flight starts from a blank one
      // rather than drawing a cliff between two unrelated legs.
      setTrace((prev) =>
        next?.alt == null ? [] : [...prev, next.alt].slice(-HISTORY),
      );
      timer = window.setTimeout(poll, POLL_MS);
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [reg]);

  if (!live) return null;

  const climbing = live.rate != null && live.rate >= RATE_DEADBAND_FPM;
  const descending = live.rate != null && live.rate <= -RATE_DEADBAND_FPM;

  return (
    <Link
      to={to}
      aria-label={[
        `${reg} is airborne`,
        live.alt != null && `at ${live.alt.toLocaleString()} feet`,
        `${live.gs} knots`,
        climbing && `climbing ${live.rate} feet per minute`,
        descending && `descending ${Math.abs(live.rate!)} feet per minute`,
        'Open the flight tracker.',
      ]
        .filter(Boolean)
        .join(', ')}
      className={`inline-flex items-center gap-2.5 rounded-full py-[7px] pl-2.5 pr-3.5
                 bg-gradient-to-b from-brand to-brand-700 shadow-machined
                 ring-1 ring-inset ring-white/10 transition-colors duration-200
                 hover:from-brand-500 hover:to-brand-600
                 dark:from-white/[0.11] dark:to-white/[0.05] dark:hover:from-white/[0.16] ${className}`}
    >
      <Heading track={live.track} />
      {trace.length >= 4 && <Trace points={trace} />}
      {live.alt != null && (
        <span className="flex items-baseline gap-[3px]">
          <span className="text-[13px] font-semibold leading-none tabular-nums text-white">
            {live.alt.toLocaleString()}
          </span>
          <span className="text-[9px] font-medium uppercase leading-none tracking-[0.08em] text-white/40">
            ft
          </span>
        </span>
      )}
      {(climbing || descending) && (
        <span
          className={`text-[9px] font-medium leading-none tabular-nums ${
            climbing ? 'text-emerald-400' : 'text-white/55'
          }`}
        >
          {climbing ? '+' : '−'}
          {Math.abs(live.rate!)}
        </span>
      )}
      <span className="h-3.5 w-px shrink-0 bg-white/[0.14]" aria-hidden />
      <span className="flex items-baseline gap-[3px]">
        <span className="text-[13px] font-semibold leading-none tabular-nums text-white">
          {live.gs}
        </span>
        <span className="text-[9px] font-medium uppercase leading-none tracking-[0.08em] text-white/40">
          kt
        </span>
      </span>
    </Link>
  );
}

/** Evenly spaced pick, keeping the first and last samples. */
function downsample(values: number[], max: number): number[] {
  if (values.length <= max) return values;
  const step = (values.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => values[Math.round(i * step)]);
}

/**
 * The aeroplane points where the aeroplane is pointed. When the feed gives us no
 * track we leave it at zero rather than inventing a heading — a wrong heading is
 * worse than a neutral one.
 */
function Heading({ track }: { track: number | null }) {
  return (
    <span
      className="grid h-[17px] w-[17px] shrink-0 place-items-center rounded-full bg-white/[0.07]"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-[10px] w-[10px] text-emerald-400 transition-transform duration-700"
        style={{ transform: `rotate(${track ?? 0}deg)` }}
      >
        <path d="M12 2c.7 0 1.2.9 1.2 2v5.6l7.8 4.6v1.9l-7.8-2.4v4.9l2.4 1.7v1.4L12 21l-3.6.7v-1.4l2.4-1.7v-4.9L3 16.1v-1.9l7.8-4.6V4c0-1.1.5-2 1.2-2z" />
      </svg>
    </span>
  );
}

/** Never scale the trace tighter than this. See Trace. */
const MIN_SPAN_FT = 300;

/**
 * The altitudes we have actually seen. It is a shape, not a gauge — no axis
 * labels, and the data is centred rather than pinned to an edge.
 *
 * The scale has a floor. Scaling to the exact min/max means that in cruise, where
 * the transponder reports 5,500 and 5,525 alternately, a 25 ft rounding wiggle
 * gets stretched to the full height of the sparkline and the airplane looks like
 * it is in a rodeo. Anything inside MIN_SPAN_FT therefore draws as the near-flat
 * line it actually is, and only a real climb or descent bends it.
 */
function Trace({ points }: { points: number[] }) {
  const W = 38;
  const H = 14;
  const PAD = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const mid = (min + max) / 2;
  const span = Math.max(max - min, MIN_SPAN_FT);
  const lo = mid - span / 2;
  const step = (W - PAD * 2) / (points.length - 1);

  const xy = points.map((v, i) => {
    const ratio = (v - lo) / span;
    return [PAD + i * step, PAD + (1 - ratio) * (H - PAD * 2)] as const;
  });
  const d = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const [lx, ly] = xy[xy.length - 1];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-[14px] w-[38px] shrink-0 overflow-visible"
      fill="none"
      aria-hidden
    >
      <path
        d={d}
        stroke="rgba(52,211,153,0.85)"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lx} cy={ly} r="1.75" fill="#34D399" />
    </svg>
  );
}
